/*
 * Akva — produktkolonnen klæber i bunden (snippets/akva-klaeb.liquid).
 *
 * Måler den klæbende produktkolonne og skriver højden i --akva-klaeb-h, så
 * CSS'en kan regne top = min(header-offset, 100dvh − højde − luft). Klassen
 * akva-klaeb slår reglen til. Uden scriptet gælder Fabrics klæbning som før.
 *
 * Højden følges med en ResizeObserver: konfiguratoren folder valg ud (Dual,
 * madras 2, Lumbar Support), og skaleringen på computer ændrer tekstens
 * størrelse, når vinduet ændres. Skærmhøjden står i CSS (100dvh) og kræver
 * intet her.
 *
 * Korte sider (8/10): kan siden ikke rulles langt nok til, at kolonnen når
 * galleriets bund, står kolonnen under headeren, og billedet fortsætter
 * nedenunder (care og tilbehør uden footerlogoet, høje skærme). Scriptet
 * regner derfor også den top ud, der lægger kolonnens bund på galleriets
 * bund, når man har rullet helt ned, og skriver den i --akva-klaeb-min:
 *
 *     galleriets bund − længste rulning − kolonnehøjde
 *
 * Kolonnen må ikke skubbes ned under sin plads øverst på siden, for så står
 * titlen lavere, når siden åbner. Mangler der rulning til det, får footerens
 * nederste sektion den manglende luft forneden (--akva-klaeb-ekstra på html),
 * ligesom logoet gav før. CSS'en bruger den største af de to toppe, så
 * siderne, der kan rulles langt nok, er uændrede. Vandmadrassernes høje
 * kolonne, der klæber i bunden, røres ikke.
 *
 * Den længste rulning måles uden luften under footeren: både den her og den,
 * footeren får, mens kurvbaren står fremme (akva-produkt.css). Baren skjules
 * ved footeren, så kolonnen hopper ikke, når den forsvinder. Sidens højde
 * følges med en ResizeObserver på sidens dele (footeren lazy-loades, billeder
 * og faner ændrer højden) og vinduets størrelse.
 *
 * Fabrics morph kan skrive kolonnens class og style forfra (et nyt produkt i
 * en kombineret listing). En MutationObserver sætter dem igen. I temaeditoren
 * tegnes sektionen forfra og findes igen ved shopify:section:load.
 */

const SELECTOR =
  '.product-information .product-details.sticky-content--desktop:not(.full-height--desktop)';
const CLASS = 'akva-klaeb';
const VAR = '--akva-klaeb-h';
const VAR_MIN = '--akva-klaeb-min';
const VAR_EXTRA = '--akva-klaeb-ekstra';

/** @type {WeakMap<HTMLElement, number>} */
const heights = new WeakMap();

/** @type {WeakMap<HTMLElement, string>} */
const minTops = new WeakMap();

/** @type {Set<HTMLElement>} */
const columns = new Set();

/** @param {HTMLElement} el */
function apply(el) {
  const height = heights.get(el);
  if (height === undefined) return;

  const value = `${height}px`;
  if (el.style.getPropertyValue(VAR) !== value) el.style.setProperty(VAR, value);

  const minTop = minTops.get(el);
  if (minTop !== undefined && el.style.getPropertyValue(VAR_MIN) !== minTop) el.style.setProperty(VAR_MIN, minTop);

  if (!el.classList.contains(CLASS)) el.classList.add(CLASS);
}

/**
 * Elementet, siden ruller i: .page-wrapper fra 990 px, ellers vinduet.
 * @param {HTMLElement} el
 */
function scroller(el) {
  for (let node = el.parentElement; node && node !== document.body; node = node.parentElement) {
    const overflow = getComputedStyle(node).overflowY;
    if ((overflow === 'auto' || overflow === 'scroll') && node.scrollHeight > node.clientHeight) return node;
  }
  return /** @type {HTMLElement} */ (document.scrollingElement || document.documentElement);
}

/**
 * Toppen, der lægger kolonnens bund på galleriets bund ved længste rulning,
 * og den luft, siden mangler forneden for at kunne det.
 * @param {HTMLElement} el
 * @returns {{ top: number | null, extra: number } | undefined}
 */
function measure(el) {
  const parent = el.parentElement;
  if (!parent) return undefined;

  const box = scroller(el);
  const isWindow = box === document.scrollingElement || box === document.documentElement;
  const portTop = isWindow ? 0 : box.getBoundingClientRect().top + box.clientTop;
  const scrollTop = box.scrollTop;
  const height = el.getBoundingClientRect().height;

  // Høj kolonne: klæber i bunden som før.
  const headerOffset = parseFloat(getComputedStyle(el).getPropertyValue('--sticky-header-offset')) || 0;
  if (height + headerOffset > box.clientHeight) return { top: null, extra: 0 };

  // Rulningen uden luften under footeren (den her og kurvbarens).
  const lastFooterSection = document.querySelector('footer > .shopify-section:last-child');
  const space = lastFooterSection ? parseFloat(getComputedStyle(lastFooterSection).paddingBlockEnd) || 0 : 0;
  const maxScroll = Math.max(0, box.scrollHeight - box.clientHeight - space);

  const rect = parent.getBoundingClientRect();
  const style = getComputedStyle(parent);
  const start = rect.top + parseFloat(style.paddingTop) - portTop + scrollTop;
  const end = rect.bottom - parseFloat(style.paddingBottom) - portTop + scrollTop;

  // 1 px dybere end regnet: scrollHeight er afrundet, og klæbningen standser
  // alligevel kolonnen ved galleriets bund, så den flugter på pixlen.
  const flush = end - maxScroll - height + 1;
  const extra = Math.max(0, flush - start);
  return { top: Math.min(flush - extra, box.clientHeight - height), extra };
}

function update() {
  let extra = 0;
  for (const el of columns) {
    if (!el.isConnected) {
      columns.delete(el);
      continue;
    }
    const result = measure(el);
    if (!result) continue;
    if (result.top === null) {
      minTops.delete(el);
      el.style.removeProperty(VAR_MIN);
    } else {
      minTops.set(el, `${result.top.toFixed(2)}px`);
    }
    extra = Math.max(extra, result.extra);
    apply(el);
  }
  const root = document.documentElement;
  const value = `${extra.toFixed(2)}px`;
  if (root.style.getPropertyValue(VAR_EXTRA) !== value) root.style.setProperty(VAR_EXTRA, value);
}

/* Efter ResizeObserverens runde: luften under footeren ændrer selv sidens
   højde, og en ændring midt i runden giver "ResizeObserver loop"-fejlen. */
let scheduled = false;
function schedule() {
  if (scheduled) return;
  scheduled = true;
  setTimeout(() => {
    scheduled = false;
    update();
  }, 0);
}

const resizeObserver = new ResizeObserver((entries) => {
  for (const entry of entries) {
    const el = /** @type {HTMLElement} */ (entry.target);
    if (!columns.has(el)) continue;
    const box = entry.borderBoxSize?.[0];
    const height = box ? box.blockSize : el.getBoundingClientRect().height;
    heights.set(el, Math.ceil(height));
    apply(el);
  }
  schedule();
});

const mutationObserver = new MutationObserver((records) => {
  for (const record of records) apply(/** @type {HTMLElement} */ (record.target));
});

/** Sidens dele, hvis højde ændrer, hvor langt man kan rulle. */
function observePage() {
  for (const part of document.querySelectorAll('body > *, .page-wrapper > *')) {
    if (part instanceof HTMLElement && !['SCRIPT', 'STYLE', 'LINK', 'TEMPLATE'].includes(part.tagName)) {
      resizeObserver.observe(part);
    }
  }
}

/** @param {ParentNode} root */
function watch(root) {
  for (const el of root.querySelectorAll(SELECTOR)) {
    if (!(el instanceof HTMLElement) || heights.has(el)) continue;
    columns.add(el);
    heights.set(el, Math.ceil(el.getBoundingClientRect().height));
    apply(el);
    resizeObserver.observe(el);
    if (el.parentElement) resizeObserver.observe(el.parentElement);
    mutationObserver.observe(el, { attributes: true, attributeFilter: ['class', 'style'] });
  }
  update();
}

observePage();
watch(document);
window.addEventListener('resize', schedule);

document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) watch(event.target);
  observePage();
});
